import React, { useMemo, useEffect, useState, useImperativeHandle, forwardRef, memo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { SortChangedEvent, CellClickedEvent, GridReadyEvent, FirstDataRenderedEvent, CellKeyDownEvent, RowNode } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import AgGridPaging from './aggrid-paging';
import { LinearProgress, useColorScheme, Backdrop, CircularProgress } from '@mui/material';
import type { DataApiState, AgGridHandle, AgGridProps } from './share/type';
import './style.css';

const defaultProps = {
    defaultValues: {
        rowData: [] as any[],
        rowCount: null,
        isSearch: false, //default = false
        isLoading: false, // on/off LinearProgress
        sortField: { field: 'id', order: 'asc' }, // default sort field
        filter: {}
    },
};
/**
 * A memoized wrapper component for AgGridReact that integrates server-side data operations like
 * filtering, sorting, and pagination.
 *
 * @param {object} props - The component props.
 *
 * @param {object} [props.defaultValues=defaultProps.defaultValues] - An object containing initial state values for the grid, such as `sortField`, `rowCount`, etc.
 *
 * @param {function} [props.onCellClicked] - A callback function that fires when a grid cell is clicked. It receives the standard AgGrid `CellClickedEvent` params. If not provided, the component handles default editing behavior.
 *
 * @param {React.ReactNode} [props.children] - Standard React children, useful for declaratively passing AgGrid features like custom cell renderers.
 *
 * @param {string} props.list - **Required.** The resource name (e.g., 'products', 'users') used by the `dataProvider` to determine the API endpoint for data fetching.
 *
 * @param {function} [props.setDefaultValues] - A callback function used to lift the grid's internal state (like sort order) up to the parent component. This is useful for external actions that need to be aware of the grid's state, such as exporting.
 *
 * @param {Array<object>} props.columnDefs - **Required.** The AgGrid column definitions array. Each object in the array configures a column (header, field, width, etc.).
 *
 * @param {Array<object>} [props.rowData] - An array of data to be displayed in the grid. If this prop is provided, the component will use this data instead of fetching it via the `dataProvider`.
 *
 * @param {Array<object>} [props.dataAction] - A callback function after search complete: return row data, page index, page count`.
 *
 * @param {Array<object>} [props.filterValue] - search criteria use for pagination after re-render`.
 *
 * @param {Array<object>} [props.pagination] - {pageindex, pagecount, total, pagesize} use for render pagination`.
 *
 * @param {Array<object>} [props.selectedRowKeys] - An array of row objects that should be selected when the grid mounts or remounts. Used to restore selection state after component unmount/remount cycles.
 *
 * @param {boolean} [props.isRaw] - If true, the component will not use the dataProvider to load data. This is useful when the parent component manages data fetching and passes the data directly via the `rowData` prop.
 * */
const AgGrid = forwardRef<AgGridHandle, AgGridProps>((props, ref) => {
    const {
        defaultValues = defaultProps.defaultValues,
        onCellClicked,
        children,
        list,
        listAction,
        setDefaultValues,
        isRaw,
        columnDefs,
        rowData,
        gridHeight,
        hidePaging,
        gridOptions,
        dataAction,
        pagination,
        filterValue,
        selectedRowKeys,
        onGridReadyCallback,
        ...restAggridProps
    } = props;
    const gridStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
    const containerStyle = useMemo(() => ({ width: '100%', height: gridHeight || '83vh' }), [gridHeight]);
    const agGridRef = React.useRef<AgGridReact>(null);

    // Proper event listener cleanup
    useEffect(() => {
        if (!props.id) return;
        
        const handleContextMenu = (event: MouseEvent) => {
            event.preventDefault();
        };
        
        const element = document.getElementById(props.id);
        element?.addEventListener('contextmenu', handleContextMenu);
        
        return () => {
            element?.removeEventListener('contextmenu', handleContextMenu);
        };
    }, [props.id]);

    const [dataApi, setDataApi] = useState<DataApiState>(() => {
        const initialState: any = { ...defaultProps.defaultValues };

        // override prop defaultValues if have
        for (const key in defaultValues) {
            initialState[key as string] = defaultValues[key as keyof typeof defaultValues];
        }

        // update filter
        initialState.filter = filterValue || {};
        
        return initialState;
    });

    useEffect(() => {
        if (filterValue !== undefined) {
            setDataApi(prev => ({ ...prev, filter: filterValue || {} }));
        }
    }, [filterValue]);

    // Disable sorting for all columns when isRaw is false, use server-side sorting instead
    if (columnDefs && isRaw === false) {
        let temp: any;
        columnDefs.forEach((col: any) => {
            do {
                temp = col;
                if (!temp.field && temp.children) {
                    temp = col.children;
                }
            }
            while ((!temp.field && temp.children));
            if (Array.isArray(temp)) {
                temp.forEach((tempCol: any) => tempCol['comparator'] = (_valueA: any, _valueB: any, _nodeA: any, _nodeB: any) => { return null; });
            } else {
                temp['comparator'] = (_valueA: any, _valueB: any, _nodeA: any, _nodeB: any) => { return null; };
            }
        });
    }
       
    const customFilterSearch = (filter: Record<string, unknown>): void => {
        setDataApi({ ...dataApi, isSearch: true, filter: filter });
    };
    const showLoading = (isShow: boolean): void => {
        setDataApi({ ...dataApi, isLoading: isShow });
    };

    const onGridReady = (params: GridReadyEvent): void => {
        // Default action here
        // ...
        // Call user's callback
        if (onGridReadyCallback) {
            onGridReadyCallback(params);
        }
    };

    const onFirstDataRendered = (params: FirstDataRenderedEvent): void => {
        if (selectedRowKeys && selectedRowKeys.length > 0) {
            const { api } = params;
            const rowKeyName = Object.keys(selectedRowKeys[0])[0];
            // Determine which row is selected
            api.forEachNode((node: RowNode) => {
                if (!node.data) return; // Skip if node data is null or undefined
                const isRowSelected = selectedRowKeys.some((rowKey: any) =>
                    rowKey[rowKeyName] === node.data[rowKeyName]
                );
                if (isRowSelected) {
                    node.setSelected(true);
                }
            });
        }
    };

    const onSortChanged = (params: SortChangedEvent): void => {
        if (isRaw === true) return; // skip if use raw data
        // Get the current sort model from the grid
        const columnState = params.columnApi?.getColumnState();
        if (!columnState) return;
        
        const sortedColumns = columnState.filter((c) => c.sort);
        const sortField = sortedColumns.length > 0
            ? { field: sortedColumns[0].colId, order: sortedColumns[0].sort!.toUpperCase() }
            : undefined;
            
        if (typeof sortField !== 'undefined') {
            let _isLoading = false;
            if (dataApi.sortField.field !== sortField.field || (dataApi.sortField.field === sortField.field && dataApi.sortField.order.toUpperCase() !== sortField.order)) {
                _isLoading = true;
            }
            setDataApi({ ...dataApi, sortField: sortField, isLoading: agGridRef.current?.api && agGridRef.current.api.getDisplayedRowCount() > 0 && _isLoading ? true : false });
            if (setDefaultValues) {
                setDefaultValues({ ...defaultValues, sortField: sortField });
            }
        }
        else {
            setDataApi({ ...dataApi, sortField: { field: dataApi.sortField.field, order: 'asc' }, isLoading: agGridRef.current?.api && agGridRef.current.api.getDisplayedRowCount() > 0 ? true : false });
        }
    };

    useImperativeHandle(ref, () => ({
        customFilterSearch: customFilterSearch,
        showLoading: showLoading,
        getAgGridInstance () { return agGridRef.current; }
    }));

    //#region For copy text in cell
    const onCellKeyDown = (params: CellKeyDownEvent): void => {
        const keyboardEvent = params.event as KeyboardEvent;
        if (keyboardEvent.ctrlKey && keyboardEvent.keyCode === 67) {
            const field = params.colDef.field;
            if (field && params.data && field in params.data) {
                const cellValue = params.data[field];
                if (cellValue !== null && cellValue !== undefined) {
                    navigator.clipboard.writeText(String(cellValue));
                }
            }
        }
    };
    const finalOptions = {
        ...gridOptions,
        suppressCellFocus: true,  // Prevents AG Grid from selecting the whole cell
        suppressClipboardPaste: true, // Prevents AG Grid from overriding clipboard paste
    };
    //#endregion

    //#region handle oncellClick
    //prevent display multiple edit-popup
    const handleCellClicked = (params: CellClickedEvent): void => {
        if (!onCellClicked) {
            //close all existing popup
            const gridApi = params.api;
            const editedCell = gridApi.getFocusedCell();
            gridApi.stopEditing();

            //re open new clicked editable cell
            if (editedCell) {
                setTimeout(() => {
                    const { rowIndex, column } = editedCell;
                    // Re-trigger the default cell click behavior
                    gridApi.startEditingCell({
                        rowIndex,
                        colKey: column.getColId(),
                    });
                }, 100);
            }
        } else {
            onCellClicked(params);
        }
    };
    //#endregion

    const { mode } = useColorScheme();
    const agTheme = 'ag-theme-alpine' + (mode === 'dark' ? '-dark' : '');
    return (
        <div style={containerStyle}>
            {(isRaw === false || isRaw === undefined) &&
                <Backdrop
                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={dataApi.isLoading}>
                    <CircularProgress color='inherit' />
                </Backdrop>
            }
            <div className={agTheme} style={gridStyle} id={props.id}>
                <AgGridReact //https://www.ag-grid.com/javascript-data-grid/grid-options
                    {...restAggridProps}
                    columnDefs={columnDefs}
                    ref={agGridRef}
                    rowData={rowData || dataApi.rowData}
                    onSortChanged={onSortChanged}
                    loadingCellRenderer={<LinearProgress />}
                    onCellKeyDown={onCellKeyDown}
                    gridOptions={finalOptions}
                    onCellClicked={handleCellClicked}
                    onGridReady={onGridReady}
                    onFirstDataRendered={onFirstDataRendered}
                >
                    {children}
                </AgGridReact>
            </div>
            {!hidePaging && <AgGridPaging
                list={list}
                listAction={listAction}
                dataApi={dataApi}
                setDataApi={setDataApi}
                callback={dataAction}
                pagination={pagination}
                isRaw={isRaw}
            />}
        </div>
    );
});

export default memo(AgGrid);